// UserContext.tsx
import { createSignal, createMemo, createContext, createResource, useContext, JSX, createEffect, onMount } from "solid-js";
import { createAsync, revalidate } from "@solidjs/router";

import { type Subscription, type LogAction, type RIASRecordType, getUserInfoQuery } from "~/lib";
import { ADMIN } from "~/_server/constants";


type User = {
	sub: string;
	email: string;
	"cognito:groups"?: string[];
	impersonating?: boolean;
	profile?: string;
};

type UserContextValue = {
	user: ReturnType<typeof createResource<User | {}>>;
	logAction: ( group: RIASRecordType, action: LogAction, metadata?: Record<string, any> ) => Promise<void>;
	subscriptions: () => [Subscription];
	inActiveSubscriptions: () => [Subscription];
	sessionId: () => number;
	impersonationId: () => number;
	setImpersonation: ( n: number ) => void;
	impersonationName: () => string | null;
	isLoggedIn: () => boolean;
	isImpersonating: () => boolean;
	userIs: ( group: string ) => boolean;
	refetchUser: () => void;
	sub: string
};

export const UserContext = createContext<UserContextValue>();

export const useRIASApp = (): UserContextValue => useContext( UserContext ) as UserContextValue;

export const UserProvider = ( props: { children: JSX.Element } ) => {
	const [impersonationId, setImpersonationId] = createSignal<number>( 0 );
	const [sessionId, setSessionId] = createSignal<number>( 0 );

	const user = createAsync( () => getUserInfoQuery() );

	createEffect( () => {
		const imp = impersonationId();
		const id = ( imp ?? 0 ) !== 0 ? imp : parseInt( user()?.profile ?? 0 );
		
		setSessionId( id );
	} );

	const userIs = ( group: string ): boolean =>
		( Array.isArray( user()?.groups ) && ( user()?.groups ).includes( group ) ) ?? false;

	const isLoggedIn = createMemo( () => Boolean( user()?.sub ) );

	const isImpersonating = createMemo( () => Boolean( impersonationId() > 0 ) );

	const logAction = async (
		group: RIASRecordType,
		action: LogAction,
		metadata: Record<string, any> = {},
	): Promise<void> => {
		/*const locationMetadata = {
		...metadata,
		pathname: location.pathname,
		search: location.search,
		state: location.state
		};*/
		const { logAction } = await import( "~/_server" );

		logAction( sessionId(), group, action, metadata, `USER#${parseInt( user()?.profile ?? 0 )}` );
	}

	createEffect( async () => {
		if ( user()?.groups.includes(ADMIN) ) {
			await fetch( '/api/impersonate', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify( { impersonateId: impersonationId() > 0 ? impersonationId() : null } )
			} );
		}
	} );

	return (
		<UserContext.Provider
			value={{
				user,
				sub: user()?.sub?? "",
				logAction,
				sessionId,
				impersonationId,
				setImpersonation: ( n: number ) => setImpersonationId( n ),
				isLoggedIn,
				isImpersonating,
				userIs,
				refetchUser: () => revalidate(getUserInfoQuery.keyFor()),
			}}
		>
			{props.children}
		</UserContext.Provider>
	);
};

